import { useMemo } from "react";
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import ArrowCircleDown from "@mui/icons-material/ArrowCircleDown";
import Draggable from "../../shared utils/Draggable.jsx";
import Droppable from "../../shared utils/Droppable.jsx";
import ResidentCard from "./ResidentCard.js";
import BedroomWarningsPanel from "./BedroomWarningsPanel.jsx";
import BedroomGapsPanel from "./BedroomGapsPanel.jsx";
import WholeStayPromptDialog from "./WholeStayPromptDialog.jsx";
import { getRoomLabel } from "../utils/roomLabelUtils";

function formatEnsuite(value) {
  if (value === true) return "Ensuite";
}

function BedroomBoardView({
  rooms,
  allRooms,
  filteredResidents,
  noCampusSelected,
  nameSearch,
  onMoveResident,
  onSave,
  isSaving,
  onDownloadFireList,
  onDownloadRoomPdf,
  onDownloadRoomSigns,
  warnings,
  onUndoWarning,
  onDismissWarning,
  onClearWarnings,
  coverageGaps,
  wholeStayPrompt,
  onWholeStay,
  onKeepFromDate,
  onCancelWholeStay,
}) {
  const matchesResidentSearch = (person) => {
    return `${person.name} ${person.surname}`
      .toLowerCase()
      .includes(String(nameSearch || "").trim().toLowerCase());
  };

  // Assigned = has a room ANYWHERE on campus, so the unassigned pool stays the
  // same regardless of the house filter. Use the full campus room list (allRooms),
  // not the house-filtered `rooms` being rendered.
  const assignedIds = useMemo(() => {
    const set = new Set();
    (allRooms || rooms).forEach((room) => {
      (room.residents || []).forEach((resident) => set.add(String(resident.id)));
    });
    return set;
  }, [allRooms, rooms]);

  const unassignedResidents = filteredResidents
    .filter((resident) => !assignedIds.has(String(resident.id)) && matchesResidentSearch(resident))
    .sort((a, b) => `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`));

  const handleDragEnd = ({ active, over }) => {
    if (!active?.id || !over?.id) return;
    const targetId = over.id === "unassigned" ? null : over.id;
    onMoveResident(active.id, targetId);
  };

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 140,
        tolerance: 8,
      },
    })
  );

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <WholeStayPromptDialog
        prompt={wholeStayPrompt}
        onWholeStay={onWholeStay}
        onKeepFromDate={onKeepFromDate}
        onCancel={onCancelWholeStay}
      />
      <BedroomGapsPanel gaps={coverageGaps} />
      <BedroomWarningsPanel
        warnings={warnings}
        onUndo={onUndoWarning}
        onDismiss={onDismissWarning}
        onClearAll={onClearWarnings}
      />
      <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mb: 3 }}>
        <Droppable key="unassigned" id="unassigned" className="room unassigned-students">
          <div className="droppable-area">
            <h3>Unassigned Residents</h3>
            {noCampusSelected && (
              <p>Select a campus to see residents.</p>
            )}
            {unassignedResidents.map((person) => (
              <Draggable key={person.id} id={person.id}>
                {({ attributes, listeners }) => (
                  <ResidentCard
                    person={person}
                    dragHandleProps={{ ...attributes, ...listeners }}
                  />
                )}
              </Draggable>
            ))}
          </div>
        </Droppable>
      </Box>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {rooms.map((room) => {
            const isFull =
              Number(room.capacity) > 0 &&
              (room.residents?.length || 0) >= Number(room.capacity);

            const visibleResidents = (room.residents || [])
              .filter(matchesResidentSearch)
              .sort((a, b) => `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`));

            const occupancyLabel = isFull
              ? "FULL"
              : `${visibleResidents.length}/${room.capacity}`;

            const ensuiteLabel = formatEnsuite(room.ensuite);

            return (
              <Droppable
                key={room.id}
                id={room.id}
                className={`room ${isFull ? "room-full" : ""}`}
              >
                <div className="droppable-area">
                  <Stack spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {getRoomLabel(room)}
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {ensuiteLabel && (
                        <Chip size="small" label={ensuiteLabel} />
                      )}
                      <Chip size="small" label={occupancyLabel} />
                    </Stack>
                  </Stack>

                  {visibleResidents.map((person) => (
                    <Draggable key={person.id} id={person.id}>
                      {({ attributes, listeners }) => (
                        <ResidentCard
                          person={person}
                          dragHandleProps={{ ...attributes, ...listeners }}
                        />
                      )}
                    </Draggable>
                  ))}

                  {String(nameSearch || "").trim() && visibleResidents.length === 0 && (
                    <p>No residents match search</p>
                  )}
                </div>
              </Droppable>
            );
          })}
        </Box>

        <Box
          sx={{
            position: "sticky",
            top: 100,
            minWidth: 180,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Button variant="contained" onClick={() => onSave(false)} disabled={isSaving}>
            {isSaving ? "Saving…" : "Save Changes"}
          </Button>
          <Button variant="outlined" onClick={onDownloadFireList}>
            Download Fire List
          </Button>
          <Button
            variant="outlined"
            onClick={onDownloadRoomPdf}
            startIcon={<ArrowCircleDown />}
          >
            Download Room PDF
          </Button>
          <Button
            variant="outlined"
            onClick={onDownloadRoomSigns}
            startIcon={<ArrowCircleDown />}
          >
            Room Signs PDF
          </Button>
        </Box>
      </Box>
    </DndContext>
  );
}

export default BedroomBoardView;